import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Target, 
  Loader2, 
  CheckCircle, 
  ArrowRight,
  Eye,
  MousePointer,
  ShoppingCart,
  Users,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  X,
  Link as LinkIcon,
  Save,
  Cloud,
  CloudOff,
  AlertOctagon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout, { useProjectSteps } from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, SectionHeader, StatusChip } from '@/components/ios26/IOSComponents';
import { PLATFORM_IDS, PLATFORMS_CONFIG, getPlatformName, getPlatformColor, isManualPlatform } from '@/lib/platformConstants';
import { 
  saveLocalDraft, 
  loadLocalDraft, 
  deleteLocalDraft, 
  DRAFT_STATE, 
  normalizePlatforms 
} from '@/lib/draftStore';
import '@/styles/ios26-design-system.css';

// Debounce hook with flush capability
function useDebouncedSave(callback, delay) {
  const timeoutRef = useRef(null);
  const pendingArgsRef = useRef(null);
  const callbackRef = useRef(callback);
  
  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  const debouncedSave = useCallback((...args) => {
    pendingArgsRef.current = args;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (pendingArgsRef.current) {
        callbackRef.current(...pendingArgsRef.current);
        pendingArgsRef.current = null;
      }
    }, delay);
  }, [delay]);
  
  // P0 FIX: Flush pending save immediately (for navigation/beforeunload)
  const flushSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingArgsRef.current) {
      callbackRef.current(...pendingArgsRef.current);
      pendingArgsRef.current = null;
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return { debouncedSave, flushSave };
}

// Get user ID from token (simple decode)
function getUserIdFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id || payload.sub || null;
  } catch (e) {
    return null;
  }
}

// Platform logos as SVG components for real branding
// P0 FIX: X logo component for reuse
const XLogoSvg = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PlatformLogos = {
  instagram: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  facebook: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  x: XLogoSvg,
  x_twitter: XLogoSvg, // P0 FIX: Add x_twitter mapping to match PLATFORM_IDS
  linkedin: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  telegram: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  ),
  whatsapp: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  blog: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ),
  brevo: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
    </svg>
  )
};

// P0 FIX: Use colors from canonical constants
const PLATFORM_COLORS_DEPRECATED = PLATFORMS_CONFIG;

/**
 * Campaign Objectives
 */
const OBJECTIVES = [
  {
    id: 'visibility',
    name: 'Visibilité',
    icon: Eye,
    description: 'Faire connaître votre marque',
    color: 'hsl(var(--color-primary))'
  },
  {
    id: 'traffic',
    name: 'Trafic',
    icon: MousePointer,
    description: 'Générer des visites sur votre site',
    color: 'hsl(270 60% 55%)'
  },
  {
    id: 'sales',
    name: 'Ventes',
    icon: ShoppingCart,
    description: 'Convertir en clients',
    color: 'hsl(var(--color-success))'
  },
  {
    id: 'engagement',
    name: 'Engagement',
    icon: Users,
    description: 'Créer une communauté active',
    color: 'hsl(var(--color-warning))'
  }
];

/**
 * Frequency Options
 */
const FREQUENCIES = [
  { id: '1/day', label: '1 par jour', desc: '30 contenus/mois' },
  { id: '3/week', label: '3 par semaine', desc: '12 contenus/mois' },
  { id: '1/week', label: '1 par semaine', desc: '4 contenus/mois' },
  { id: 'custom', label: 'Personnalisé', desc: 'Définir manuellement' }
];

/**
 * Step 3: Créer une campagne
 * ==========================
 * USES SEMANTIC TOKENS for dark mode compatibility
 * 
 * P0 POINT 3: UX Rule for Unvalidated Themes
 * - Persistent warning banner if themes not validated
 * - Confirmation modal when clicking "Valider la campagne" if themes not validated
 * - Campaign creation is ALLOWED (flexible rule), but user is warned
 */
function CampaignStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { completeStep, stepStatuses, refreshProject } = useProjectSteps();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftState, setDraftState] = useState(DRAFT_STATE.IDLE);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('1/day');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [campaignCreated, setCampaignCreated] = useState(false);
  const [channelsStatus, setChannelsStatus] = useState({});
  const [themesValidated, setThemesValidated] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  
  // P0 POINT 3: Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // P0 FIX TASK B: Modal for unconnected platform selection
  const [showConnectPrompt, setShowConnectPrompt] = useState(null);
  // P0 FIX: Track if user has made any selection (to prevent auto-save of pre-selections)
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  
  const userId = getUserIdFromToken();
  
  // P0 FIX: Save draft to backend with proper state handling
  const saveDraftToBackend = useCallback(async (objective, frequency, platforms) => {
    if (campaignCreated) return;
    
    setDraftState(DRAFT_STATE.SAVING);
    
    try {
      const response = await apiClient.post(`/projects/${projectId}/campaign/draft`, {
        objective,
        frequency,
        platforms: normalizePlatforms(platforms) // Always send canonical IDs
      });
      
      if (response.data?.ok) {
        setDraftState(DRAFT_STATE.SAVED);
        // Also save to localStorage as backup
        saveLocalDraft(userId, projectId, { objective, frequency, platforms });
      } else {
        throw new Error('Draft save failed');
      }
    } catch (error) {
      console.error('Failed to save draft to backend:', error);
      // Fallback to localStorage
      if (saveLocalDraft(userId, projectId, { objective, frequency, platforms })) {
        setDraftState(DRAFT_STATE.OFFLINE);
      } else {
        setDraftState(DRAFT_STATE.ERROR);
      }
    }
    
    // Reset state after 2 seconds
    setTimeout(() => {
      setDraftState(prev => prev === DRAFT_STATE.SAVED || prev === DRAFT_STATE.OFFLINE ? DRAFT_STATE.IDLE : prev);
    }, 2000);
  }, [projectId, userId, campaignCreated]);
  
  // Debounced save with flush capability (400ms)
  const { debouncedSave, flushSave } = useDebouncedSave(saveDraftToBackend, 400);
  
  // P0 FIX: Auto-save on changes - BUT NOT on initial hydration
  useEffect(() => {
    if (!draftLoaded || campaignCreated) return;
    
    // P0 FIX: Only save after user has explicitly interacted
    // This prevents the bug where pre-selected connected platforms are auto-saved
    if (!userHasInteracted) return;
    
    // Save draft whenever selections change (only after user interaction)
    debouncedSave(selectedObjective, selectedFrequency, selectedPlatforms);
  }, [selectedObjective, selectedFrequency, selectedPlatforms, draftLoaded, campaignCreated, debouncedSave, userHasInteracted]);
  
  // P0 FIX: Flush save on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushSave();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushSave]);
  
  useEffect(() => {
    loadProjectAndDraft();
  }, [projectId]);
  
  // P1 FIX: Listen for X connection events to refresh platforms
  useEffect(() => {
    const handleXConnected = (event) => {
      console.log('[CampaignStep] P1 FIX: Received ame:x-connected event, refreshing platforms...');
      loadProjectAndDraft();
    };
    
    window.addEventListener('ame:x-connected', handleXConnected);
    return () => window.removeEventListener('ame:x-connected', handleXConnected);
  }, [projectId]);
  
  // P0 FIX: Load project status AND draft with proper pre-selection
  // P1 FIX: Use /api/integrations/summary as SINGLE SOURCE OF TRUTH for connected platforms
  const loadProjectAndDraft = async () => {
    try {
      // P1 FIX: Fetch integrations summary FIRST (single source of truth)
      let integrationsPlatforms = {};
      try {
        const integrationsResponse = await apiClient.get(`/integrations/summary?project_id=${projectId}`);
        if (integrationsResponse.data?.ok) {
          integrationsPlatforms = integrationsResponse.data.platforms || {};
          console.log('[CampaignStep] P1 FIX: Using /api/integrations/summary as source of truth', 
            integrationsResponse.data.connected_platforms);
        }
      } catch (intErr) {
        console.warn('[CampaignStep] Could not fetch integrations summary, falling back to project status');
      }
      
      // P1 FIX: Fetch Instagram status directly (same as ConnectStep) for consistency
      // This ensures both screens use the exact same source of truth
      let igConnectedFromStatus = false;
      let igUsernameFromStatus = null;
      try {
        const igStatusResp = await apiClient.get('/social/instagram/status');
        const igStatus = igStatusResp.data;
        console.info('[CampaignStep] Instagram status from /social/instagram/status:', {
          publish_ready: igStatus.publish_ready,
          selected_ig_business_id: igStatus.selected_ig_business_id,
          selected_page_id: igStatus.selected_page_id,
          blocking_reason: igStatus.blocking_reason
        });
        // P1 FIX: EXACT SAME LOGIC AS CONNECTSTEP
        igConnectedFromStatus = igStatus.publish_ready === true || 
                                (igStatus.selected_ig_business_id && igStatus.selected_page_id);
        igUsernameFromStatus = igStatus.ig_username || igStatus.selected_ig_username;
        
        // Override integrationsPlatforms.instagram with this status
        if (igConnectedFromStatus) {
          integrationsPlatforms.instagram = {
            ...integrationsPlatforms.instagram,
            platform: 'instagram',
            platform_id: 'instagram',
            connected: true,
            configured: true,
            enabled: true,
            username: igUsernameFromStatus
          };
          console.info('[CampaignStep] ✅ Instagram CONNECTED via /social/instagram/status');
        }
      } catch (igErr) {
        console.warn('[CampaignStep] Could not fetch Instagram status:', igErr);
      }
      
      // Fetch project status
      const statusResponse = await apiClient.get(`/projects/${projectId}/status`);
      const status = statusResponse.data;
      
      setThemesValidated(status.steps?.themes_validated === true);
      
      // P1 FIX: Merge channels from project status with integrations summary
      // Priority: integrations summary > project.channels_config
      const mergedChannels = {};
      
      // First, get channels from project status
      Object.entries(status.channels || {}).forEach(([id, ch]) => {
        mergedChannels[id] = { ...ch };
      });
      
      // Then, override with integrations summary (single source of truth)
      Object.entries(integrationsPlatforms).forEach(([id, platform]) => {
        mergedChannels[id] = {
          ...mergedChannels[id],
          connected: platform.connected,
          configured: platform.configured,
          enabled: platform.enabled,
          x_username: platform.username,
          auth_type: platform.auth_type
        };
        // Also update under canonical platform_id if different
        if (platform.platform_id && platform.platform_id !== id) {
          mergedChannels[platform.platform_id] = {
            ...mergedChannels[platform.platform_id],
            connected: platform.connected,
            configured: platform.configured,
            enabled: platform.enabled,
            x_username: platform.username,
            auth_type: platform.auth_type
          };
        }
      });
      
      setChannelsStatus(mergedChannels);
      
      // P1 FIX: Log for debugging
      const connectedPlatformsList = Object.entries(mergedChannels)
        .filter(([_, ch]) => ch.connected)
        .map(([id]) => id);
      console.log('[CampaignStep] P1 FIX: Merged channels status | connected:', connectedPlatformsList);
      
      // Get list of connected platform IDs
      const connectedIds = connectedPlatformsList;
      
      // P1 FIX: Only lock if campaign is FINALIZED (has content generated), not just configured
      // A campaign_configured=true but content_generated=false means user saved preferences but hasn't generated yet
      const isCampaignFinalized = status.campaign?.configured && status.steps?.content_generated;
      
      if (isCampaignFinalized) {
        console.info('[CampaignStep] Campaign is FINALIZED - locking form');
        setCampaignCreated(true);
        setSelectedObjective(status.campaign.objective);
        setSelectedFrequency(status.campaign.frequency || '1/day');
        setSelectedPlatforms(normalizePlatforms(status.campaign.platforms) || []);
        setDraftLoaded(true);
        setLoading(false);
        return;
      }
      
      // If campaign is configured but not finalized, pre-fill but allow editing
      if (status.campaign?.configured && !isCampaignFinalized) {
        console.info('[CampaignStep] Campaign configured but not finalized - allowing edits');
        setSelectedObjective(status.campaign.objective);
        setSelectedFrequency(status.campaign.frequency || '1/day');
        setSelectedPlatforms(normalizePlatforms(status.campaign.platforms) || []);
        // Do NOT set campaignCreated to true - allow editing
      }
      
      // P0 FIX: Fetch draft from backend
      let draftHydrated = false;
      try {
        const draftResponse = await apiClient.get(`/projects/${projectId}/campaign/draft`);
        const draft = draftResponse.data?.draft;
        
        if (draft && !draft.is_finalized) {
          // Hydrate from backend draft
          if (draft.objective) setSelectedObjective(draft.objective);
          if (draft.frequency) setSelectedFrequency(draft.frequency);
          
          // P0 FIX: If draft has platforms, use them. DO NOT auto-select connected ones
          // This prevents the bug where all connected platforms are generated
          if (draft.platforms?.length > 0) {
            setSelectedPlatforms(normalizePlatforms(draft.platforms));
          }
          // P0 FIX: Removed auto-selection of connected platforms
          // Users must explicitly select which platforms to use
          
          draftHydrated = true;
        }
      } catch (draftError) {
        console.log('No backend draft found');
      }
      
      // Fallback: Try localStorage
      if (!draftHydrated) {
        const localDraft = loadLocalDraft(userId, projectId);
        if (localDraft) {
          if (localDraft.objective) setSelectedObjective(localDraft.objective);
          if (localDraft.frequency) setSelectedFrequency(localDraft.frequency);
          
          // P0 FIX: Only use saved platforms, DO NOT auto-select
          if (localDraft.platforms?.length > 0) {
            setSelectedPlatforms(normalizePlatforms(localDraft.platforms));
          }
          // P0 FIX: Removed auto-selection of connected platforms
          
          draftHydrated = true;
        }
      }
      
      // P0 FIX: No draft at all - DO NOT pre-select any platforms
      // Users must explicitly choose which platforms to use
      // Removed: setSelectedPlatforms(connectedIds);
      
      setDraftLoaded(true);
    } catch (error) {
      console.error('Failed to load project status:', error);
      toast.error('Erreur lors du chargement du projet');
      setDraftLoaded(true);
    } finally {
      setLoading(false);
    }
  };
  
  // P0 FIX TASK B: Check if a platform is connected (from backend status)
  const isPlatformConnected = (platformId) => {
    return channelsStatus[platformId]?.connected === true;
  };
  
  // P0 FIX TASK B: Get list of connected platforms
  const getConnectedPlatforms = () => {
    return Object.entries(channelsStatus)
      .filter(([_, ch]) => ch.connected)
      .map(([id]) => id);
  };
  
  // P0 POINT 3: Handle campaign creation with confirmation if themes not validated
  const handleCreateCampaignClick = () => {
    if (!selectedObjective) {
      toast.error('Sélectionnez un objectif');
      return;
    }
    
    if (selectedPlatforms.length === 0) {
      toast.error('Sélectionnez au moins une plateforme');
      return;
    }
    
    // P0 FIX TASK B: Check if ALL selected platforms are connected
    const unconnectedSelected = selectedPlatforms.filter(p => !isPlatformConnected(p));
    if (unconnectedSelected.length > 0) {
      const names = unconnectedSelected.map(getPlatformName).join(', ');
      toast.error(`Plateforme(s) non connectée(s): ${names}. Connectez-les d'abord.`, {
        action: {
          label: 'Connecter',
          onClick: () => navigate(`/projects/${projectId}/setup/connect`)
        }
      });
      return;
    }
    
    // P0 FIX TASK B: Verify at least one platform is actually connected
    const connectedSelected = selectedPlatforms.filter(isPlatformConnected);
    if (connectedSelected.length === 0) {
      toast.error('Impossible de valider: connectez au moins une plateforme.');
      return;
    }
    
    // If themes not validated, show confirmation modal
    if (!themesValidated) {
      setShowConfirmModal(true);
      return;
    }
    
    // Otherwise, proceed directly
    handleCreateCampaign();
  };
  
  const handleCreateCampaign = async () => {
    setShowConfirmModal(false);
    
    // P0 FIX: Flush pending draft save before creating campaign
    flushSave();
    
    setSaving(true);
    
    try {
      // P0 FIX: Send normalized platforms (canonical IDs)
      const response = await apiClient.post(`/projects/${projectId}/campaign`, {
        objective: selectedObjective,
        frequency: selectedFrequency,
        platforms: normalizePlatforms(selectedPlatforms)
      });
      
      // P0 POINT 1: Check response format (ok/success)
      if (response.data.ok || response.data.success) {
        toast.success('Campagne créée avec succès !');
        setCampaignCreated(true);
        completeStep('campaign');
        
        // P0 FIX: Delete draft after campaign finalized
        try {
          await apiClient.delete(`/projects/${projectId}/campaign/draft`);
          deleteLocalDraft(userId, projectId);
        } catch (e) {
          // Silent fail - draft cleanup is not critical
        }
        
        // Refresh project to update sidebar
        if (refreshProject) {
          await refreshProject();
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || errorData?.detail || 'Erreur lors de la création de la campagne';
      toast.error(errorMessage);
      console.error('Campaign creation error:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // P0 FIX: Handle continue navigation with flush
  const handleContinue = () => {
    flushSave(); // Flush pending save
    navigate(`/projects/${projectId}/setup/generate`);
  };
  
  // P0 FIX TASK B: Toggle platform with unconnected check
  const togglePlatform = (platformId) => {
    if (campaignCreated) return;
    
    // Check if platform is connected before selecting
    if (!isPlatformConnected(platformId) && !selectedPlatforms.includes(platformId)) {
      // Show prompt to connect instead of selecting
      setShowConnectPrompt(platformId);
      return;
    }
    
    // P0 FIX: Mark that user has interacted (for auto-save logic)
    setUserHasInteracted(true);
    
    setSelectedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(var(--color-primary))' }} />
      </div>
    );
  }
  
  // P0 POINT 3: Persistent warning banner for unvalidated themes
  const showThemesWarning = !themesValidated && !campaignCreated;
  
  return (
    <div className="max-w-3xl mx-auto">
      <SectionHeader
        title="Créer une campagne"
        subtitle="Définissez vos objectifs et préférences de publication"
      />
      
      {/* P0 FIX: Draft saved indicator with proper state feedback */}
      {!campaignCreated && draftLoaded && (selectedObjective || selectedPlatforms.length > 0) && (
        <div 
          className="flex items-center gap-2 mb-4 text-xs"
          style={{ color: 'hsl(var(--color-text-muted))' }}
        >
          {draftState === DRAFT_STATE.SAVING && (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Enregistrement...</span>
            </>
          )}
          {draftState === DRAFT_STATE.SAVED && (
            <>
              <Cloud className="w-3 h-3" style={{ color: 'hsl(var(--color-success))' }} />
              <span style={{ color: 'hsl(var(--color-success))' }}>Enregistré</span>
            </>
          )}
          {draftState === DRAFT_STATE.OFFLINE && (
            <>
              <CloudOff className="w-3 h-3" style={{ color: 'hsl(var(--color-warning))' }} />
              <span style={{ color: 'hsl(var(--color-warning))' }}>Hors ligne — enregistré sur cet appareil</span>
            </>
          )}
          {draftState === DRAFT_STATE.ERROR && (
            <>
              <AlertOctagon className="w-3 h-3" style={{ color: 'hsl(var(--color-destructive))' }} />
              <span style={{ color: 'hsl(var(--color-destructive))' }}>Erreur d&apos;enregistrement</span>
            </>
          )}
          {draftState === DRAFT_STATE.IDLE && (
            <>
              <Save className="w-3 h-3" />
              <span>Brouillon auto-enregistré</span>
            </>
          )}
        </div>
      )}
      
      {/* P0 POINT 3: Persistent themes warning banner */}
      {showThemesWarning && (
        <div 
          className="flex items-start gap-3 p-4 rounded-xl mb-6"
          style={{ 
            backgroundColor: 'hsl(var(--color-warning-bg))',
            border: '1px solid hsl(var(--color-warning) / 0.3)'
          }}
          data-testid="themes-warning-banner"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--color-warning))' }} />
          <div className="flex-1">
            <p className="font-medium text-sm" style={{ color: 'hsl(var(--color-warning-text))' }}>
              Thèmes non validés — risque de contenu hors-sujet
            </p>
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--color-warning-text))' }}>
              Nous recommandons de valider les thèmes marketing extraits pour garantir que le contenu généré soit pertinent pour votre marque.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <PillButton
              variant="outline"
              size="sm"
              onClick={() => navigate(`/projects/${projectId}/setup/analyze`)}
              data-testid="validate-themes-btn"
            >
              Valider les thèmes
            </PillButton>
          </div>
        </div>
      )}
      
      {/* Objective Selection */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-semantic mb-4">
          Quel est votre objectif principal ?
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          {OBJECTIVES.map((obj) => {
            const isSelected = selectedObjective === obj.id;
            const Icon = obj.icon;
            
            return (
              <button
                key={obj.id}
                onClick={() => {
                  if (campaignCreated) return;
                  setUserHasInteracted(true);
                  setSelectedObjective(obj.id);
                }}
                disabled={campaignCreated}
                className="p-5 rounded-2xl text-left transition-all"
                style={{
                  backgroundColor: isSelected 
                    ? 'hsl(var(--color-info-bg))' 
                    : 'hsl(var(--color-surface-2))',
                  border: isSelected 
                    ? '2px solid hsl(var(--color-primary))' 
                    : '2px solid transparent',
                  opacity: campaignCreated ? 0.75 : 1,
                  cursor: campaignCreated ? 'not-allowed' : 'pointer'
                }}
                data-testid={`objective-${obj.id}`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ 
                      backgroundColor: isSelected ? obj.color : 'hsl(var(--color-surface-3))',
                      color: isSelected ? 'white' : 'hsl(var(--color-text-muted))'
                    }}
                  >
                    {isSelected ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <Icon className="w-6 h-6" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-semantic">{obj.name}</h4>
                    <p className="text-sm text-semantic-muted">{obj.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Frequency Selection */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-semantic mb-4">
          Fréquence de publication
        </h3>
        
        <div className="grid md:grid-cols-2 gap-3">
          {FREQUENCIES.map((freq) => {
            const isSelected = selectedFrequency === freq.id;
            
            return (
              <button
                key={freq.id}
                onClick={() => {
                  if (campaignCreated) return;
                  setUserHasInteracted(true);
                  setSelectedFrequency(freq.id);
                }}
                disabled={campaignCreated}
                className="p-4 rounded-xl text-left transition-all"
                style={{
                  backgroundColor: isSelected 
                    ? 'hsl(var(--color-info-bg))' 
                    : 'hsl(var(--color-surface-2))',
                  border: isSelected 
                    ? '2px solid hsl(var(--color-primary))' 
                    : '2px solid transparent',
                  opacity: campaignCreated ? 0.75 : 1,
                  cursor: campaignCreated ? 'not-allowed' : 'pointer'
                }}
                data-testid={`frequency-${freq.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-semantic">{freq.label}</p>
                    <p className="text-sm text-semantic-muted">{freq.desc}</p>
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-5 h-5" style={{ color: 'hsl(var(--color-primary))' }} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Platform Selection with Real Logos - P0 FIX: Use canonical PLATFORM_IDS */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-semantic mb-4">
          Plateformes à utiliser
        </h3>
        
        <div className="flex flex-wrap gap-3">
          {PLATFORM_IDS.map((platform) => {
            const isSelected = selectedPlatforms.includes(platform);
            const isConnected = isPlatformConnected(platform);
            const Logo = PlatformLogos[platform];
            const platformConfig = PLATFORMS_CONFIG[platform];
            
            return (
              <button
                key={platform}
                onClick={() => togglePlatform(platform)}
                disabled={campaignCreated}
                className="px-4 py-2.5 rounded-full font-medium transition-all flex items-center gap-2"
                style={{
                  backgroundColor: isSelected 
                    ? getPlatformColor(platform) 
                    : 'hsl(var(--color-surface-2))',
                  color: isSelected 
                    ? 'white' 
                    : 'hsl(var(--color-text))',
                  border: isSelected 
                    ? `2px solid ${getPlatformColor(platform)}` 
                    : '2px solid hsl(var(--color-border))',
                  opacity: campaignCreated ? 0.75 : (!isConnected ? 0.7 : 1),
                  cursor: campaignCreated ? 'not-allowed' : 'pointer'
                }}
                title={!isConnected ? 'Connectez ce canal d\'abord' : ''}
                data-testid={`platform-${platform}`}
              >
                {Logo ? <Logo /> : null}
                {getPlatformName(platform)}
                {isConnected ? (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                ) : (
                  <span 
                    className="text-xs px-1.5 py-0.5 rounded flex items-center gap-1"
                    style={{ 
                      backgroundColor: 'hsl(var(--color-warning-bg))',
                      color: 'hsl(var(--color-warning-text))'
                    }}
                  >
                    <LinkIcon className="w-3 h-3" />
                    Non connecté
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        {getConnectedPlatforms().length === 0 && (
          <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'hsl(var(--color-warning-bg))' }}>
            <p className="text-sm" style={{ color: 'hsl(var(--color-warning-text))' }}>
              ⚠️ Aucune plateforme connectée. 
              <button 
                onClick={() => navigate(`/projects/${projectId}/setup/connect`)}
                className="underline ml-1 font-medium"
              >
                Connectez vos comptes
              </button> 
              {' '}dans l&apos;étape précédente pour activer la publication automatique.
            </p>
          </div>
        )}
        
        {/* WhatsApp manual mode info */}
        {selectedPlatforms.includes('whatsapp') && (
          <div 
            className="mt-4 p-3 rounded-lg flex items-start gap-2"
            style={{ 
              backgroundColor: 'hsl(var(--color-info-bg))',
              border: '1px solid hsl(var(--color-primary) / 0.2)'
            }}
            data-testid="whatsapp-manual-info"
          >
            <span className="text-lg">📋</span>
            <div>
              <p className="text-sm font-medium" style={{ color: 'hsl(var(--color-text))' }}>
                Publication WhatsApp : manuel (copier/coller)
              </p>
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--color-text-muted))' }}>
                WhatsApp ne dispose pas d&apos;API de publication. Vous copierez le contenu pour le coller manuellement.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* P0 FIX TASK B: Connect prompt modal */}
      {showConnectPrompt && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowConnectPrompt(null)}
        >
          <div 
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: 'hsl(var(--color-surface))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="connect-prompt-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: getPlatformColor(showConnectPrompt) }}
              >
                {PlatformLogos[showConnectPrompt] && React.createElement(PlatformLogos[showConnectPrompt])}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-semantic">
                  {getPlatformName(showConnectPrompt)} non connecté
                </h3>
                <p className="text-sm text-semantic-muted mt-1">
                  Connectez ce canal pour l&apos;utiliser dans votre campagne.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <PillButton
                variant="primary"
                className="flex-1"
                onClick={() => {
                  setShowConnectPrompt(null);
                  navigate(`/projects/${projectId}/setup/connect`);
                }}
                data-testid="go-connect-btn"
              >
                Aller connecter
              </PillButton>
              <PillButton
                variant="outline"
                className="flex-1"
                onClick={() => setShowConnectPrompt(null)}
                data-testid="cancel-connect-btn"
              >
                Annuler
              </PillButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Summary */}
      {selectedObjective && (
        <GlassCard 
          className="p-5 mb-8"
          style={{ 
            background: 'linear-gradient(135deg, hsl(var(--color-info-bg)) 0%, hsl(270 50% 95%) 100%)',
            border: '1px solid hsl(var(--color-primary) / 0.2)'
          }}
        >
          <div className="flex items-start gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--color-primary))' }}
            >
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-semantic mb-1">Résumé de votre campagne</h3>
              <ul className="text-sm text-semantic-2 space-y-1">
                <li>• Objectif : <strong className="text-semantic">{OBJECTIVES.find(o => o.id === selectedObjective)?.name}</strong></li>
                <li>• Fréquence : <strong className="text-semantic">{FREQUENCIES.find(f => f.id === selectedFrequency)?.label}</strong></li>
                <li>• Plateformes : <strong className="text-semantic">{selectedPlatforms.length} sélectionnée{selectedPlatforms.length > 1 ? 's' : ''}</strong></li>
              </ul>
            </div>
          </div>
        </GlassCard>
      )}
      
      {/* Action Buttons */}
      {!campaignCreated ? (
        <PillButton
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleCreateCampaignClick}
          loading={saving}
          disabled={!selectedObjective || selectedPlatforms.length === 0}
          icon={<CheckCircle className="w-5 h-5" />}
          data-testid="create-campaign-btn"
        >
          {saving ? 'Création en cours...' : 'Valider la campagne'}
        </PillButton>
      ) : (
        <div className="space-y-4">
          <StatusChip status="success" className="mx-auto">
            <CheckCircle className="w-4 h-4 mr-1" />
            Campagne créée
          </StatusChip>
          <PillButton
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleContinue}
            icon={<ArrowRight className="w-5 h-5" />}
            iconPosition="right"
            data-testid="continue-to-generate-btn"
          >
            Continuer vers la génération
          </PillButton>
        </div>
      )}
      
      {/* P0 POINT 3: Confirmation modal for unvalidated themes */}
      {showConfirmModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div 
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: 'hsl(var(--color-surface))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="themes-confirm-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'hsl(var(--color-warning-bg))' }}
              >
                <AlertTriangle className="w-6 h-6" style={{ color: 'hsl(var(--color-warning))' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-semantic">
                  Continuer sans valider les thèmes ?
                </h3>
                <p className="text-sm text-semantic-muted mt-1">
                  Les thèmes marketing n&apos;ont pas été validés. Le contenu généré pourrait être moins pertinent.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <PillButton
                variant="primary"
                className="flex-1"
                onClick={() => {
                  setShowConfirmModal(false);
                  navigate(`/projects/${projectId}/setup/analyze`);
                }}
                data-testid="modal-validate-themes-btn"
              >
                Valider les thèmes
              </PillButton>
              <PillButton
                variant="outline"
                className="flex-1"
                onClick={handleCreateCampaign}
                data-testid="modal-continue-btn"
              >
                Continuer
              </PillButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Campaign Step Page
 */
export default function CampaignStep() {
  const { projectId } = useParams();
  
  return (
    <GuidedProjectLayout projectId={projectId}>
      <CampaignStepContent />
    </GuidedProjectLayout>
  );
}
